import { createReducer } from '@reduxjs/toolkit';

import { vehicleLandingFacetField } from 'Addons/fitmentSearch/vehicleLandingFacetField';
import { vehicleDiscardingRequested, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import {
  discard,
  discardField,
  discardManyFields,
  replaceColor,
  replaceManyValues,
  replaceRequest,
  replaceValue,
  resetRequest,
  toggle,
  toggleColor,
} from 'Core/actions/request';
import { setResponse } from 'Core/actions/response';
import { prepareResponseFacets } from 'Core/reducers/search/response';
import { FacetValue, type FacetValueBase, WeightedColorsArray } from 'Models/index';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { distribute, uniqueStrings } from 'Utils/array';

const initState: FacetValueBase[] = [];

export default createReducer(initState, (builder) =>
  builder
    .addCase(discard, (state, { payload: { value } }) => discardEntry(state, value))
    .addCase(discardField, (state, { payload: { field } }) => discardFieldEntries(state, field))
    .addCase(discardManyFields, (state, { payload: { fields } }) => discardManyFieldEntries(state, fields))
    .addCase(replaceManyValues, (state, { payload: { values } }) => replaceManyValuesEntries(state, values))
    .addCase(replaceColor, (state, { payload: { value } }) => replaceColorEntry(state, value))
    .addCase(replaceRequest, (state, { payload: { selection, protectedFields } }) =>
      replaceRequestEntries(state, selection ?? [], protectedFields),
    )
    .addCase(replaceValue, (state, { payload: { value } }) => replaceManyValuesEntries(state, [value]))
    .addCase(resetRequest, () => [])
    .addCase(toggle, (state, { payload: { value, isSingleValue } }) =>
      toggleEntry(state, { value, isSingleValue }),
    )
    .addCase(toggleColor, (state, { payload: { value } }) => toggleColorEntry(state, value))
    .addCase(
      setResponse,
      (_state, { payload: { Facets }, meta: { preselection } }) =>
        prepareResponseFacets(Facets, preselection, (f) => !fitmentSearchConfig.fields.includes(f.FieldName))
          .selection,
    )
    .addCase(vehicleSelected, (state) => handleVehicleLandingField(state))
    .addCase(vehicleDiscardingRequested, (state) => handleVehicleLandingField(state)),
);

function handleVehicleLandingField(state: FacetValueBase[]): FacetValueBase[] {
  if (state.some((f) => f.field === vehicleLandingFacetField)) {
    return discardFieldEntries(state, vehicleLandingFacetField);
  }

  return state;
}

function discardEntry(state: FacetValueBase[], value: FacetValueBase): FacetValueBase[] {
  return discardChildFields(
    removeFromSelection(state, FacetValue.valueKey, FacetValue.valueKey(value)),
    value.field,
  );
}

function discardFieldEntries(state: FacetValueBase[], field: string): FacetValueBase[] {
  return discardChildFields(removeFromSelection(state, 'field', field), field);
}

function discardManyFieldEntries(state: FacetValueBase[], fields: string[]): FacetValueBase[] {
  return fields.reduce(discardFieldEntries, state);
}

function replaceManyValuesEntries(state: FacetValueBase[], values: FacetValueBase[]): FacetValueBase[] {
  const fields = uniqueStrings(values.map(({ field }) => field));
  const addedValues = values.filter((v) => v.isSelected ?? true);

  return [...fields.reduce(discardFieldEntries, state), ...addedValues];
}

function replaceColorEntry(state: FacetValueBase[], value: FacetValueBase): FacetValueBase[] {
  return state.reduce((newState: FacetValueBase[], sel) => {
    if (!FacetValue.equal(sel, value)) {
      return [...newState, sel];
    }
    return (value.isSelected ?? true) ? [...newState, value] : newState;
  }, []);
}

function replaceRequestEntries(
  state: FacetValueBase[],
  selection: FacetValueBase[],
  protectedFields: string[],
): FacetValueBase[] {
  if (selection && selection.length > 0) {
    return selection;
  }

  return state.filter((sel) => protectedFields.includes(sel.field));
}

function toggleEntry(
  state: FacetValueBase[],
  { value, isSingleValue }: { value: FacetValueBase; isSingleValue?: boolean },
): FacetValueBase[] {
  const uniqueValue = value.isUnique && state.find((sel) => sel.isUnique && sel.field === value.field);
  if (FacetValue.isInCollection(value, state)) {
    return discardEntry(state, value);
  }
  if (value.isSelected ?? true) {
    if (isSingleValue) {
      return replaceManyValuesEntries(state, [value]);
    }
    if (uniqueValue) {
      return [...discardEntry(state, uniqueValue), value];
    }
    return [...state, value];
  }
  return state;
}

function toggleColorEntry(state: FacetValueBase[], value: FacetValueBase): FacetValueBase[] {
  const wasSelected = FacetValue.isInCollection(value, state);
  if (wasSelected || (value.isSelected ?? true)) {
    const [colorSelection, otherSelection] = distribute(state, (sel) => sel.field === value.field);
    const newColorSelection = WeightedColorsArray.toggleAndNormNotModified(
      colorSelection,
      value,
      wasSelected,
    );
    return [...newColorSelection, ...otherSelection];
  }
  return state;
}

// helper functions

function discardChildFields(state: FacetValueBase[], field: string): FacetValueBase[] {
  return discardManyFieldEntries(state, facetsConfig.getChildrenForField(field).childFields);
}

const getByKey = (key: keyof FacetValueBase | ((o: FacetValueBase) => unknown)) => (o: FacetValueBase) =>
  typeof key === 'function' ? key(o) : o[key];

function removeFromSelection(
  state: FacetValueBase[],
  key: keyof FacetValueBase | ((o: FacetValueBase) => unknown),
  value: unknown,
): FacetValueBase[] {
  const get = getByKey(key);
  return state.filter((sel) => get(sel) !== value);
}
